import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { X, Loader2, AlertCircle, Info, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const SCORE_UNITS = ['%', 'wcpm', 'points', 'rating', 'frequency', 'problems correct', 'words', 'other'];

// Helper to get score unit from intervention
const getInterventionScoreUnit = (intervention) => {
  if (!intervention) return '%';
  return intervention.score_unit || '%';
};

export default function AddProgressModal({ 
  studentId, 
  studentName, 
  measureName, 
  interventions = [], // List of student's interventions
  selectedInterventionId = null, // Pre-selected intervention (when opened from intervention row)
  onClose, 
  onSave 
}) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Get initial intervention
  const initialIntervention = selectedInterventionId 
    ? interventions.find(i => i.id === selectedInterventionId) 
    : null;
  
  const [formData, setFormData] = useState({
    student_id: studentId,
    intervention_id: selectedInterventionId || '',
    date_collected: new Date().toISOString().split('T')[0],
    measure_name: measureName || '',
    score: '', // Always start blank
    score_unit: getInterventionScoreUnit(initialIntervention),
    note: ''
    // Note: entered_by is NOT in formData - backend sets from authenticated user
  });

  // Update score_unit when intervention changes
  useEffect(() => {
    if (formData.intervention_id) {
      const intervention = interventions.find(i => i.id === formData.intervention_id);
      if (intervention) {
        setFormData(prev => ({
          ...prev,
          score_unit: getInterventionScoreUnit(intervention)
        }));
      }
    }
  }, [formData.intervention_id, interventions]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.intervention_id) {
      toast.error('Please select an intervention');
      return;
    }

    if (!formData.measure_name || !formData.score) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      await apiService.createProgress({
        ...formData,
        score: parseFloat(formData.score)
      });
      toast.success('Progress data added successfully');
      onSave();
    } catch (error) {
      console.error('Failed to create progress:', error);
      toast.error(error.response?.data?.detail || 'Failed to add progress data');
    } finally {
      setLoading(false);
    }
  };

  // Get intervention name for display
  const selectedIntervention = interventions.find(i => i.id === formData.intervention_id);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="add-progress-modal">
        <div className="modal-header">
          <h2 className="modal-title">Add Data</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="summary-box mb-4">
              <p className="text-sm text-[#595959]">
                Adding data for <strong>{studentName}</strong>
              </p>
            </div>

            {/* No interventions warning */}
            {interventions.length === 0 && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">No interventions found</p>
                  <p className="text-sm text-amber-700">Please add an intervention first before adding data.</p>
                </div>
              </div>
            )}

            <div className="form-grid">
              {/* Intervention Selection - Required */}
              <div className="form-group full-width">
                <Label htmlFor="intervention_id">Intervention *</Label>
                <Select 
                  value={formData.intervention_id} 
                  onValueChange={(v) => handleChange('intervention_id', v)}
                  disabled={interventions.length === 0}
                >
                  <SelectTrigger data-testid="intervention-select">
                    <SelectValue placeholder="Select an intervention" />
                  </SelectTrigger>
                  <SelectContent>
                    {interventions.map((intervention) => (
                      <SelectItem key={intervention.id} value={intervention.id}>
                        {intervention.intervention_name} 
                        {intervention.active_status !== 'Active' && (
                          <span className="text-[#A3A3A3] ml-1">({intervention.active_status})</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Baseline Reference Box - Shows when intervention is selected */}
              {selectedIntervention && (
                <div className="form-group full-width">
                  <div className="p-3 bg-[#F3F4F1] border border-[#E5E5E5] rounded-lg" data-testid="baseline-reference">
                    <div className="flex items-center gap-2 mb-2">
                      <Info className="h-4 w-4 text-[#595959]" />
                      <span className="text-sm font-medium text-[#1C1C1C]">Baseline Reference</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-[#595959]">Baseline Score:</span>
                        <span className="ml-2 font-semibold text-slate-900">
                          {selectedIntervention.baseline_score ?? '—'}
                        </span>
                      </div>
                      <div>
                        <span className="text-[#595959]">Unit:</span>
                        <span className="ml-2 font-medium text-[#1C1C1C]">
                          {formData.score_unit}
                        </span>
                      </div>
                      <div>
                        <span className="text-[#595959]">Target Skill:</span>
                        <span className="ml-2 font-medium text-[#1C1C1C]">
                          {selectedIntervention.target_skill || '—'}
                        </span>
                      </div>
                      {selectedIntervention.goal_score && (
                        <div>
                          <span className="text-[#595959]">Goal:</span>
                          <span className="ml-2 font-semibold text-emerald-600">
                            {selectedIntervention.goal_score}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="form-group">
                <Label htmlFor="date_collected">Date Collected *</Label>
                <Input
                  id="date_collected"
                  type="date"
                  value={formData.date_collected}
                  onChange={(e) => handleChange('date_collected', e.target.value)}
                  required
                  data-testid="date-collected-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="measure_name">Measure Name *</Label>
                <Input
                  id="measure_name"
                  value={formData.measure_name}
                  onChange={(e) => handleChange('measure_name', e.target.value)}
                  placeholder="e.g., DIBELS ORF"
                  required
                  data-testid="measure-name-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="score">Score *</Label>
                <Input
                  id="score"
                  type="number"
                  step="0.1"
                  value={formData.score}
                  onChange={(e) => handleChange('score', e.target.value)}
                  placeholder="Enter score"
                  required
                  data-testid="score-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="score_unit">Score Unit</Label>
                <Select value={formData.score_unit} onValueChange={(v) => handleChange('score_unit', v)}>
                  <SelectTrigger data-testid="score-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCORE_UNITS.map((unit) => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Entered By - Read-only display of logged-in user */}
              <div className="form-group full-width">
                <Label htmlFor="entered_by">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Entered By
                  </div>
                </Label>
                <div className="px-3 py-2 bg-[#F3F4F1] border border-[#E5E5E5] rounded-lg" data-testid="entered-by-display">
                  <p className="text-sm font-medium text-[#1C1C1C]">{user?.name || user?.email || 'Unknown User'}</p>
                  {user?.email && user?.name && (
                    <p className="text-xs text-[#595959]">{user.email}</p>
                  )}
                </div>
                <p className="text-xs text-[#A3A3A3] mt-1">Data entry is attributed to your logged-in account</p>
              </div>

              <div className="form-group full-width">
                <Label htmlFor="note">Note</Label>
                <Textarea
                  id="note"
                  value={formData.note}
                  onChange={(e) => handleChange('note', e.target.value)}
                  placeholder="Optional note about this data point"
                  rows={2}
                  data-testid="note-input"
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#2C4C3B] hover:bg-[#1E3629]"
              disabled={loading || interventions.length === 0}
              data-testid="save-progress-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Add Data Point'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
